/**
 * DOM safeguards — defensive monkey-patch of Node.prototype.removeChild and
 * Node.prototype.insertBefore to prevent crashes caused by third-party DOM
 * mutators (Google Translate, Grammarly, password managers, screen readers)
 * that wrap or move text nodes after React captured a parent reference.
 *
 * This is a well-documented React + Google Translate compatibility issue:
 *   https://github.com/facebook/react/issues/11538
 *
 * Strategy:
 *   - Wrap each call in a try/catch.
 *   - If the child to remove is no longer our descendant, silently no-op
 *     instead of throwing the "NotFoundError" that crashes the React tree
 *     and bubbles up to the global ErrorBoundary.
 *
 * Side-effect free import: call applyDomSafeguards() once from index.js.
 */

let applied = false;

export function applyDomSafeguards() {
  if (applied || typeof window === "undefined") return;
  if (!window.Node || !window.Node.prototype) return;
  applied = true;

  const originalRemoveChild = window.Node.prototype.removeChild;
  // eslint-disable-next-line func-names
  window.Node.prototype.removeChild = function safeRemoveChild(child) {
    if (child && child.parentNode === this) {
      try {
        return originalRemoveChild.apply(this, arguments);
      } catch (err) {
        // Swallow the rare race where another DOM mutator (e.g. Google
        // Translate) moved the node between our parentNode check and the
        // native call. Returning the child mimics the original signature.
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV !== "production") {
          console.warn("[domSafeguards] removeChild swallowed:", err?.message);
        }
        return child;
      }
    }
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[domSafeguards] removeChild called with foreign child — skipped to avoid React crash."
      );
    }
    return child;
  };

  const originalInsertBefore = window.Node.prototype.insertBefore;
  // eslint-disable-next-line func-names
  window.Node.prototype.insertBefore = function safeInsertBefore(newNode, referenceNode) {
    if (referenceNode == null || referenceNode.parentNode === this) {
      try {
        return originalInsertBefore.apply(this, arguments);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("[domSafeguards] insertBefore swallowed:", err?.message);
        }
        // Best effort fallback: append at end of parent if reference is invalid.
        try {
          return this.appendChild(newNode);
        } catch (_) {
          return newNode;
        }
      }
    }
    // referenceNode exists but is no longer a child of `this` (DOM was
    // mutated externally). Fall back to appendChild rather than throwing.
    try {
      return this.appendChild(newNode);
    } catch (_) {
      return newNode;
    }
  };
}
